import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaChevronRight, FaTimes } from "react-icons/fa";
import "react-tooltip/dist/react-tooltip.css";
import { db } from "../../firebase/firebase.utils";
import {
  cancelInvite,
  loadMemberProfiles,
} from "../../firebase/memberFunctions";
import permissions from "../../utils/permissions";
import RetroButton from "../retro/button/retro-button.component";
import TinyContextMenu from "../tiny-context-menu/TinyContextMenu";
import Member, { InviteMemberPicker } from "./member/member.component";
import PrMembers from "./prmembers.component";
import InlineAddBtn from "../InlineAddBtn";
import "./members.styles.scss";

const { getOwnerUid, getPermissions, getInviteRoleKey, ROLE_LABELS } =
  permissions;

// A scope key resets local dialogs/data when the user switches workspaces/accounts.
export default function Members({ activeSpaceData, display, showProjectMembers=false }) {
  const activeSpaceId = useSelector((state) => state.history.spaceId);
  const uid = useSelector((state) => state.user.currentUser?.uid);
  const spaceId =
    activeSpaceId || activeSpaceData?.spaceId || activeSpaceData?.id;
  return (
    <MembersPanel
      key={`${uid || "signed-out"}:${spaceId || "none"}`}
      initialSpace={activeSpaceData}
      spaceId={spaceId}
      uid={uid}
      display={display}
      showProjectMembers={showProjectMembers}
    />
  );
}

function MembersPanel({ initialSpace, spaceId, uid, display, showProjectMembers }) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const stationData = useSelector((state) => state.space.stationData) || [];
  const initialSpaceId = initialSpace?.spaceId || initialSpace?.id;
  const startingSpace =
    initialSpace && initialSpaceId === spaceId
      ? { ...initialSpace, spaceId }
      : null;
  const [space, setSpace] = useState(startingSpace);
  const [stations, setStations] = useState(() =>
    stationData
      .filter((station) => station.fromSpaceId === spaceId)
      .map((station) => ({
        ...station,
        stationId: station.stationId || station.id,
      })),
  );
  const [people, setPeople] = useState({});
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(!startingSpace);
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(false);
  // const [showProjectMembers, setShowProjectMembers] = useState(false);
  const [showPending, setShowPending] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const busyRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const visible = display !== "none";
  const access = getPermissions(space, null, uid);

  useEffect(() => {
    if (!uid || !spaceId || !visible) {
      setLoading(false);
      return;
    }
    return db
      .collection("space")
      .doc(spaceId)
      .onSnapshot(
        (snapshot) => {
          setSpace(snapshot.exists ? { ...snapshot.data(), spaceId } : null);
          if (!snapshot.exists) setError("Workspace unavailable.");
          setLoading(false);
        },
        (err) => {
          setSpace(null);
          setError(err.message);
          setLoading(false);
        },
      );
  }, [uid, spaceId, visible]);

  useEffect(() => {
    if (!uid || !spaceId || !visible || !access.canViewWorkspace) return;
    return db
      .collection("space")
      .doc(spaceId)
      .collection("stations")
      .onSnapshot(
        (snapshot) => {
          setStations(
            snapshot.docs.map((document) => ({
              ...document.data(),
              id: document.id,
              stationId: document.id,
              fromSpaceId: spaceId,
            })),
          );
        },
        (err) => setError(err.message),
      );
  }, [uid, spaceId, visible, access.canViewWorkspace]);

  useEffect(() => {
    if (!uid || !spaceId || !visible) return;
    return db
      .collection("invites")
      .where("inviterUid", "==", uid)
      .where("status", "==", "pending")
      .where("spaceId", "==", spaceId)
      .onSnapshot(
        (snapshot) =>
          setPending(
            snapshot.docs.map((document) => ({
              ...document.data(),
              id: document.id,
            })),
          ),
        (err) => setError(err.message),
      );
  }, [uid, spaceId, visible]);

  const workspaceUids = [
    ...new Set([getOwnerUid(space), ...(space?.members || [])].filter(Boolean)),
  ];
  const profileKey = JSON.stringify(
    [
      ...new Set([
        ...workspaceUids,
        ...stations.flatMap((station) => station.members || []),
        ...pending.map((item) => item.inviteeUid),
      ]),
    ].sort(),
  );
  useEffect(() => {
    let active = true;
    if (!uid || !visible) return;
    loadMemberProfiles(JSON.parse(profileKey))
      .then((result) => {
        if (active) setPeople(result);
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [profileKey, uid, visible]);
  const knownPeople = useMemo(
    () => ({
      ...people,
      ...(currentUser?.uid
        ? { [currentUser.uid]: { ...people[currentUser.uid], ...currentUser } }
        : {}),
    }),
    [people, currentUser],
  );
  const members = workspaceUids.map((id) => knownPeople[id] || { uid: id });

  async function cancel(id) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusyId(id);
    setError("");
    try {
      await cancelInvite(id);
    } catch (err) {
      setError(err.message || "Couldn't cancel invitation.");
    } finally {
      busyRef.current = false;
      setBusyId(null);
    }
  }
  function openInbox() {
    const params = new URLSearchParams(location.search);
    params.set("invites", "1");
    params.delete("inviteId");
    navigate({
      pathname: location.pathname,
      search: `?${params}`,
      hash: location.hash,
    });
  }

  return (
    <div className="members" style={{ display }}>
      <div className="members__contentBox">
        {!!error && (
          <p role="alert" className="members__error">
            {error}
          </p>
        )}
        {loading && <p role="status">Loading members…</p>}
        {!loading && !access.canViewWorkspace && (
          <p>Select a workspace you belong to.</p>
        )}
        {access.canViewWorkspace && (
          <>
            {!showProjectMembers ? (
              <div className="members__workspacelevel">
                <div className="members__list">
                  {members.map((member) => (
                    <Member
                      key={member.uid}
                      member={member}
                      activeSpaceData={space}
                      activeSpaceId={spaceId}
                    />
                  ))}
                </div>
                {access.canManageWorkspaceMembers && (
                  <>
                    <div className="members__button">
                      <InlineAddBtn onClick={() => setInvite(true)}>
                        Invite to workspace
                      </InlineAddBtn>
                    </div>
                    {invite && (
                      <InviteMemberPicker
                        space={space}
                        pending={pending}
                        onClose={() => setInvite(false)}
                      />
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="members__projectlevel">
                {stations.map((station) => (
                  <div className="project_members" key={station.stationId}>
                    <PrMembers
                      station={station}
                      space={space}
                      memberData={members}
                      people={knownPeople}
                      pending={pending}
                    />
                  </div>
                ))}
                {stations.length === 0 && (
                  <p>No projects in this workspace yet.</p>
                )}
              </div>
            )}
            <details
              className="members__pending"
              open={showPending}
              onToggle={(event) => setShowPending(event.currentTarget.open)}
            >
              <summary>
                Your pending invitations <span>({pending.length})</span>
                <FaChevronRight size={12} />
              </summary>
              {pending.length === 0 && (
                <p className="members__help">No pending invitations.</p>
              )}
              {pending.map((item) => (
                <div className="members__pending-row" key={item.id}>
                  <div>
                    <strong>
                      {knownPeople[item.inviteeUid]?.userName ||
                        item.inviteeName ||
                        item.inviteeUid}
                    </strong>
                    <p>
                      {item.targetName ||
                        (item.targetType === "space"
                          ? space.name
                          : stations.find(
                              (station) => station.stationId === item.stationId,
                            )?.name) ||
                        "Project unavailable"}
                      {" · "}
                      {ROLE_LABELS[getInviteRoleKey(item)]}
                    </p>
                  </div>
                  <TinyContextMenu
                    items={[
                      {
                        key: "cancel",
                        label:
                          busyId === item.id
                            ? "Cancelling…"
                            : "Cancel invitation",
                        icon: <FaTimes />,
                        disabled: Boolean(busyId),
                        onPress: () => cancel(item.id),
                      },
                    ]}
                  />
                </div>
              ))}
            </details>
          </>
        )}
        <div className="members__button">
          <RetroButton type="button" mode="gray" onClick={openInbox}>
            My invitations
          </RetroButton>
        </div>
      </div>
    </div>
  );
}
